import { Injectable, signal } from '@angular/core';
import { BirthdayApiService } from './birthday-api.service';
import { AssistantBirthday, BirthdayFilters, BirthdayResponse, BirthdayMeta, BirthdayRange, BirthdayMetaLink } from 'src/app/models/core/birthdays.model';
import { ProductApiService } from '../product/product-api.service';
import { PromotionalEvent } from 'src/app/models/core/event.model';

@Injectable({
  providedIn: 'root'
})
export class BirthdayService {

  readonly loading = signal(false);
  /** "Ver más": separado de `loading`, que es la carga inicial y muestra el skeleton. */
  readonly loadingMore = signal(false);
  readonly assistantsBirthdays = signal<AssistantBirthday[]>([]);
  readonly paginationMeta = signal<BirthdayMeta | null>(null);
  readonly currentRange = signal<string>('this_week');
  readonly currentPage = signal<number>(1);
  readonly perPage = signal<number>(40);
  readonly rangeInfo = signal<BirthdayRange | null>(null);

  readonly promotionalEvents = signal<PromotionalEvent[]>([]);
  readonly loadingPromotionalEvents = signal(false);

  constructor(
    private birthdayApiService: BirthdayApiService,
    private productApiService: ProductApiService,
  ) { }

  initialLoad() {
    this.loadAssistantsBirthdays({ range: this.currentRange(), per_page: 40 });
    this.loadPromotionalEvents();
  }

  loadAssistantsBirthdays(filters?: BirthdayFilters): void {
    this.loading.set(true);

    if (filters?.range) {
      this.currentRange.set(filters.range);
    }

    if (filters?.page) {
      this.currentPage.set(filters.page);
    }

    if (filters?.per_page) {
      this.perPage.set(filters.per_page);
    }

    this.birthdayApiService.getBirthdays(filters).subscribe({
      next: (response: BirthdayResponse) => {
        if (filters?.page === 1 || !filters?.page) {
          this.assistantsBirthdays.set(response.data);
        } else {
          const currentBirthdays = this.assistantsBirthdays();
          this.assistantsBirthdays.set([...currentBirthdays, ...response.data]);
        }
        this.paginationMeta.set(response.meta);
        this.rangeInfo.set(response.meta.range);
      },
      error: (error) => {
        console.error("Error loading birthdays:", error);
        this.loading.set(false);
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  // Change the range of dates
  changeRange(range: string): void {
    this.currentPage.set(1);
    this.loadAssistantsBirthdays({ range, page: 1, per_page: 40 });
  }

  // Load more birthdays (for pagination)
  loadMoreBirthdays(): void {
    // Un clic por página: mientras hay una en curso, los clics extra no piden de nuevo.
    if (this.loadingMore()) {
      return;
    }

    const nextPage = this.currentPage() + 1;
    if (nextPage <= this.getLastPage()) {
      this.loadMoreBirthdaysPage(nextPage);
    }
  }

  private loadMoreBirthdaysPage(page: number): void {
    this.loadingMore.set(true);

    this.birthdayApiService.getBirthdays({
      range: this.currentRange(),
      page,
      per_page: 40
    }).subscribe({
      next: (response: BirthdayResponse) => {
        const currentBirthdays = this.assistantsBirthdays();
        this.assistantsBirthdays.set([...currentBirthdays, ...response.data]);
        this.paginationMeta.set(response.meta);
        this.rangeInfo.set(response.meta.range);
        // Se avanza recién con la respuesta: si falla, el próximo intento pide la misma página.
        this.currentPage.set(page);
      },
      error: (error) => {
        console.error("Error loading more birthdays:", error);
        this.loadingMore.set(false);
      },
      complete: () => {
        this.loadingMore.set(false);
      },
    });
  }

  // Get total count
  getTotalCount(): number {
    return this.paginationMeta()?.total || 0;
  }

  // Get last page
  getLastPage(): number {
    return this.paginationMeta()?.last_page || 1;
  }

  // Check if has next page
  hasNextPage(): boolean {
    return (this.paginationMeta()?.current_page || 1) < (this.paginationMeta()?.last_page || 1);
  }

  // Get current range info
  getCurrentRangeInfo(): BirthdayRange | null {
    const rangeInfo = this.rangeInfo();
    return rangeInfo;
  }

  public loadPromotionalEvents(): void {
    this.loadingPromotionalEvents.set(true);


    this.productApiService.getEventsWithPromotionalProducts().subscribe({
      next: (response: { data: PromotionalEvent[] }) => {
        this.promotionalEvents.set(response.data);
        this.loadingPromotionalEvents.set(false);
      },
      error: (error) => {
        console.error("Error loading more birthdays:", error);
      }
    });
  }

  public resetAllState(): void {
    this.assistantsBirthdays.set([]);
    this.paginationMeta.set(null);
    this.currentRange.set('this_week');
    this.currentPage.set(1);
    this.perPage.set(40);
    this.rangeInfo.set(null);
    this.promotionalEvents.set([]);
    this.loading.set(false);
    this.loadingMore.set(false);
    this.loadingPromotionalEvents.set(false);
  }

}